import { useEffect, useState } from 'react'

type AuthState = 'checking' | 'authenticated' | 'unauthenticated'

type SessionUser = {
  name?: string | null
  email?: string | null
  image?: string | null
  canViewAnalytics?: boolean
} | null

type CachedSession = {
  state: AuthState
  user: SessionUser
}

// Module-level cache shared by every hook instance so mounting/unmounting a
// consumer (e.g. the mobile menu, which unmounts on close) doesn't refire the
// session fetch. Safe because login/logout in this app are always full-page
// redirects, never a same-page auth transition.
let cached: CachedSession = { state: 'checking', user: null }
let inFlight: Promise<void> | null = null
let fetchAttempts = 0
const MAX_FETCH_ATTEMPTS = 3
const FETCH_TIMEOUT_MS = 10_000
const listeners = new Set<(session: CachedSession) => void>()

function setCached(session: CachedSession): void {
  cached = session
  listeners.forEach((listener) => listener(session))
}

// Sync memoization gate: concurrent callers share one in-flight fetch. The async
// body lives in `runSessionFetch` so the network flow reads as straight-line
// async/await rather than a promise chain. The attempt cap is enforced here at
// the launch site, so no caller can exceed MAX_FETCH_ATTEMPTS.
function fetchSession(): Promise<void> {
  if (inFlight) return inFlight
  if (fetchAttempts >= MAX_FETCH_ATTEMPTS) return Promise.resolve()
  fetchAttempts += 1
  inFlight = runSessionFetch()
  return inFlight
}

async function runSessionFetch(): Promise<void> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    const res = await fetch('/api/auth/session', { signal: controller.signal })
    if (!res.ok) throw new Error(`session request failed: ${res.status}`)
    const session = await res.json()
    setCached(
      session?.user?.id
        ? { state: 'authenticated', user: session.user }
        : { state: 'unauthenticated', user: null },
    )
  } catch {
    // Transport failure, non-2xx, parse error, or timeout/abort — NOT a
    // definitive "logged out" (that arrives as a 200 with no user, handled above
    // and cached). Clear inFlight and leave the state as 'checking' so the next
    // interaction retries, up to a bound, rather than caching a wrong logged-out
    // state — or hanging in 'checking' on a stalled request — until reload.
    inFlight = null
    if (fetchAttempts < MAX_FETCH_ATTEMPTS) {
      gateArmed = false
      armSessionGate()
    }
  } finally {
    clearTimeout(timeout)
  }
}

// Events that signal a real user is present. Mirrors useDeferredActivation and
// FullStoryAnalytics: headless Lighthouse fires none of these, so gating the
// `/api/auth/session` fetch behind the first interaction deterministically
// excludes it from audits (no LCP/TBT competition, and no mid-audit "Log in"
// pill / avatar pop-in to dent CLS), while real users trigger it the moment they
// show signs of life — well before they could act on a logged-in-only affordance.
const USER_INTERACTION_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
  'mousemove',
] as const

let gateArmed = false

function removeInteractionListeners(): void {
  USER_INTERACTION_EVENTS.forEach((event) =>
    window.removeEventListener(event, handleFirstInteraction),
  )
}

async function handleFirstInteraction(): Promise<void> {
  removeInteractionListeners()
  await fetchSession()
}

// Self-contained one-shot gate: the first consumer to mount arms it, the first
// interaction fires the fetch. Idempotent via `gateArmed` + `inFlight`, so any
// number of consumers mounting/unmounting only ever produce a single fetch. The
// hook owns its own deferral — no caller needs to wire a trigger.
function armSessionGate(): void {
  if (gateArmed || cached.state !== 'checking') return
  gateArmed = true
  USER_INTERACTION_EVENTS.forEach((event) =>
    window.addEventListener(event, handleFirstInteraction, { passive: true, once: true }),
  )
}

function useSession(): CachedSession {
  const [session, setSession] = useState<CachedSession>(cached)

  useEffect(() => {
    listeners.add(setSession)
    setSession(cached)
    armSessionGate()
    return () => {
      listeners.delete(setSession)
    }
  }, [])

  return session
}

// Returns 'checking' until the session fetch resolves, so callers can avoid
// flashing logged-out-only UI (e.g. a "Log in" button) for logged-in users.
export function useIsLoggedIn(): AuthState {
  return useSession().state
}

// Returns the session user (name/email), or null while checking/unauthenticated.
export function useSessionUser(): SessionUser {
  return useSession().user
}
